import React, { useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Checkbox,
    message,
    Popover,
    Spin,
    Statistic,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { FilterOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import axios from '../../utilities/axios';
import AdminAnalyticsQueryBar, { CUSTOM_PRESET_KEY } from './AdminAnalyticsQueryBar';
import { TRADING_RANGE_PRESETS } from './adminAnalyticsPresets';

const NY_TZ = 'America/New_York';

const nyNow = () => dayjs().tz(NY_TZ);

const toNyCalendarDateString = (value) =>
    dayjs.tz(value.format('YYYY-MM-DD'), 'YYYY-MM-DD', NY_TZ).format('YYYY-MM-DD');

const { Text } = Typography;

const formatCurrency = (value) => {
    const amount = Number(value) || 0;
    const prefix = amount < 0 ? '-$' : '$';
    return `${prefix}${Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const StatTitle = ({ title, tip }) => (
    <span>
        {title}{' '}
        <Tooltip title={tip}>
            <InfoCircleOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
        </Tooltip>
    </span>
);

StatTitle.propTypes = {
    title: PropTypes.string.isRequired,
    tip: PropTypes.string.isRequired
};

const pnlRender = (value) => (
    <span style={{ color: value >= 0 ? '#389e0d' : '#cf1322', fontWeight: 600 }}>
        {formatCurrency(value)}
    </span>
);

const AccountLink = ({ accountUniqueId }) => (
    <Link to={`/admin/${accountUniqueId}`}>{accountUniqueId}</Link>
);

AccountLink.propTypes = {
    accountUniqueId: PropTypes.string.isRequired
};

const symbolColumns = [
    { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 88 },
    { title: 'Trades', dataIndex: 'totalTrades', key: 'totalTrades', width: 64 },
    {
        title: 'Net P/L',
        dataIndex: 'totalPnL',
        key: 'totalPnL',
        width: 100,
        render: pnlRender
    },
    {
        title: 'Win %',
        dataIndex: 'winRate',
        key: 'winRate',
        width: 64,
        render: (value) => `${value}%`
    }
];

const commissionDragColumns = [
    ...symbolColumns.slice(0, 3),
    {
        title: 'Commissions',
        dataIndex: 'totalCommissions',
        key: 'totalCommissions',
        width: 100,
        render: (value) => formatCurrency(value)
    },
    {
        title: 'Drag %',
        dataIndex: 'commissionDragPct',
        key: 'commissionDragPct',
        width: 72,
        render: (value) => (value == null ? '—' : `${value}%`)
    }
];

const accountLeaderboardColumns = [
    {
        title: '#',
        key: 'rank',
        width: 48,
        align: 'center',
        render: (_value, _row, index) => index + 1
    },
    {
        title: 'Account',
        dataIndex: 'accountUniqueId',
        key: 'accountUniqueId',
        ellipsis: { showTitle: true },
        render: (value) => <AccountLink accountUniqueId={value} />
    },
    {
        title: 'Email',
        dataIndex: 'userEmail',
        key: 'userEmail',
        width: 140,
        ellipsis: { showTitle: true },
        render: (value) => value || '—'
    },
    {
        title: 'Trades',
        dataIndex: 'totalTrades',
        key: 'totalTrades',
        width: 72,
        align: 'right'
    },
    {
        title: 'Period net P/L',
        dataIndex: 'periodNetPnL',
        key: 'periodNetPnL',
        width: 108,
        align: 'right',
        render: pnlRender
    },
    {
        title: 'Total net P/L',
        dataIndex: 'totalNetPnL',
        key: 'totalNetPnL',
        width: 108,
        align: 'right',
        render: (value) => (value == null ? '—' : pnlRender(value))
    }
];

const accountColumns = [
    {
        title: 'Account',
        dataIndex: 'accountUniqueId',
        key: 'accountUniqueId',
        width: 140,
        ellipsis: true,
        render: (value) => <AccountLink accountUniqueId={value} />
    },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 100 },
    { title: 'Trades', dataIndex: 'totalTrades', key: 'totalTrades', width: 72 },
    {
        title: 'Net P/L',
        dataIndex: 'totalPnL',
        key: 'totalPnL',
        width: 110,
        render: pnlRender
    }
];

const payoutFlagColumns = [
    ...accountColumns,
    {
        title: 'Largest win',
        dataIndex: 'largestWin',
        key: 'largestWin',
        width: 110,
        render: (value) => formatCurrency(value)
    },
    {
        title: '% of total',
        dataIndex: 'largestWinPct',
        key: 'largestWinPct',
        width: 88,
        render: (value) => <Tag color="orange">{value}%</Tag>
    },
    {
        title: 'Adj. P/L',
        dataIndex: 'adjustedPnL',
        key: 'adjustedPnL',
        width: 100,
        render: pnlRender
    }
];

const largestWinTradeColumns = [
    {
        title: 'Account',
        dataIndex: 'accountUniqueId',
        key: 'accountUniqueId',
        render: (value) => <AccountLink accountUniqueId={value} />
    },
    { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 80 },
    {
        title: 'Win',
        dataIndex: 'netPnL',
        key: 'netPnL',
        width: 96,
        render: pnlRender
    },
    {
        title: 'Closed',
        dataIndex: 'closeTime',
        key: 'closeTime',
        width: 110,
        render: (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—')
    }
];

const sessionColumns = [
    { title: 'Session', dataIndex: 'label', key: 'label' },
    { title: 'Trades', dataIndex: 'totalTrades', key: 'totalTrades', width: 72 },
    {
        title: 'Net P/L',
        dataIndex: 'totalPnL',
        key: 'totalPnL',
        width: 100,
        render: pnlRender
    }
];

const dayColumns = [
    { title: 'Day', dataIndex: 'label', key: 'label', width: 80 },
    { title: 'Trades', dataIndex: 'totalTrades', key: 'totalTrades', width: 72 },
    {
        title: 'Net P/L',
        dataIndex: 'totalPnL',
        key: 'totalPnL',
        width: 100,
        render: pnlRender
    }
];

const holdTimeColumns = [
    { title: 'Hold time', dataIndex: 'label', key: 'label' },
    { title: 'Trades', dataIndex: 'totalTrades', key: 'totalTrades', width: 72 },
    {
        title: 'Net P/L',
        dataIndex: 'totalPnL',
        key: 'totalPnL',
        width: 100,
        render: pnlRender
    }
];

const sideColumns = [
    { title: 'Side', dataIndex: 'side', key: 'side', width: 80 },
    { title: 'Trades', dataIndex: 'totalTrades', key: 'totalTrades', width: 72 },
    {
        title: 'Net P/L',
        dataIndex: 'totalPnL',
        key: 'totalPnL',
        width: 100,
        render: pnlRender
    },
    {
        title: 'Win %',
        dataIndex: 'winRate',
        key: 'winRate',
        width: 72,
        render: (value) => `${value}%`
    }
];

const multiAccountColumns = [
    {
        title: 'Email',
        dataIndex: 'userEmail',
        key: 'userEmail',
        width: 220,
        ellipsis: true
    },
    { title: 'Accounts', dataIndex: 'accountCount', key: 'accountCount', width: 90 },
    {
        title: 'With violation',
        dataIndex: 'violationCount',
        key: 'violationCount',
        width: 120
    },
    {
        title: 'Account IDs',
        dataIndex: 'accounts',
        key: 'accounts',
        render: (accounts) => <AccountTagsCell accounts={accounts} />
    }
];

const orderColumns = [
    { title: 'Ticker', dataIndex: 'ticker', key: 'ticker', width: 90 },
    { title: 'Fills', dataIndex: 'fillCount', key: 'fillCount', width: 72 },
    { title: 'Qty', dataIndex: 'totalQuantity', key: 'totalQuantity', width: 90 }
];

const LONG_LIST_TABLE_PAGINATION = {
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['10', '25', '50', '100'],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
    hideOnSinglePage: true
};

const AccountTagsCell = ({ accounts = [], violationKey = 'riskViolation' }) => (
    <div className="admin-trading-analytics-tag-list">
        {accounts.map((account) => (
            <Tag key={account.accountUniqueId} color={account[violationKey] ? 'red' : 'default'}>
                <Link to={`/admin/${account.accountUniqueId}`}>{account.accountUniqueId}</Link>
            </Tag>
        ))}
    </div>
);

AccountTagsCell.propTypes = {
    accounts: PropTypes.arrayOf(
        PropTypes.shape({
            accountUniqueId: PropTypes.string.isRequired,
            riskViolation: PropTypes.bool
        })
    ),
    violationKey: PropTypes.string
};

const AnalyticsTableBlock = ({ title, hint, children, layout = 'default' }) => (
    <div
        className={`admin-trading-analytics-table-block admin-trading-analytics-table-block--${layout}`}
    >
        <div className="admin-trading-analytics-table-title">{title}</div>
        {hint && (
            <Text type="secondary" className="admin-trading-analytics-table-hint">
                {hint}
            </Text>
        )}
        <div className="admin-trading-analytics-table-scroll">{children}</div>
    </div>
);

AnalyticsTableBlock.propTypes = {
    title: PropTypes.string.isRequired,
    hint: PropTypes.string,
    children: PropTypes.node.isRequired,
    layout: PropTypes.oneOf(['default', 'full', 'half'])
};

const ANALYTICS_TIMEOUT_MS = 5 * 60 * 1000;

const ELIGIBLE_OPEN_FILTER_TIP =
    'Includes accounts that were admin-open and payout-eligible on at least one day in the selected range (not just currently open).';

const fetchTradingAnalytics = ({
    startDate,
    endDate,
    types,
    eligibleOnly,
    positivePnLOnly,
    excludeOverPayoutCap,
    capAccountPeriodPnLAt10k,
    startedAfter
}) =>
    axios
        .get('crud/funded-accounts/trading-analytics', {
            params: {
                startDate,
                endDate,
                types: types.join(','),
                eligibleOnly,
                positivePnLOnly,
                excludeOverPayoutCap,
                capAccountPeriodPnLAt10k,
                ...(startedAfter ? { startedAfter } : {})
            },
            timeout: ANALYTICS_TIMEOUT_MS
        })
        .then((response) => response.data);

const mapSelectedTypeToApiTypes = (selectedType) => {
    const normalized = (selectedType || 'ALL').toUpperCase();
    if (normalized === 'PCT') return ['pct'];
    if (normalized === 'PTT') return ['ptt'];
    if (normalized === 'SIMULATOR_TTF') return ['simulator_ttf'];
    return ['simulator_ttf', 'pct', 'ptt'];
};

const resolveSymbolConcentrationMetrics = (concentration, summary, topTraded) => {
    const symbols = concentration?.symbols || {};
    const totalTrades = summary?.totalTrades || 0;

    if (Object.prototype.hasOwnProperty.call(symbols, 'top5PnLSharePct')) {
        return {
            top5PnLSharePct: symbols.top5PnLSharePct ?? 0,
            top5TradesSharePct: symbols.top5TradesSharePct ?? 0
        };
    }

    if (symbols.top5SharePct != null) {
        let { top5TradesSharePct } = symbols;
        if (
            top5TradesSharePct == null &&
            Array.isArray(topTraded) &&
            topTraded.length &&
            totalTrades > 0
        ) {
            const top5TradeCount = topTraded
                .slice(0, 5)
                .reduce((sum, row) => sum + (row.totalTrades || 0), 0);
            top5TradesSharePct = Number(((top5TradeCount / totalTrades) * 100).toFixed(1));
        }

        return {
            top5PnLSharePct: symbols.top5SharePct,
            top5TradesSharePct: top5TradesSharePct ?? 0
        };
    }

    if (!Array.isArray(topTraded) || !topTraded.length) {
        return { top5PnLSharePct: 0, top5TradesSharePct: 0 };
    }

    const positiveSymbols = topTraded.filter((row) => (row.totalPnL || 0) > 0);
    const totalPositivePnL = positiveSymbols.reduce((sum, row) => sum + (row.totalPnL || 0), 0);
    const top5ByPositivePnL = [...positiveSymbols]
        .sort((a, b) => b.totalPnL - a.totalPnL)
        .slice(0, 5);
    const top5PositiveCombined = top5ByPositivePnL.reduce(
        (sum, row) => sum + (row.totalPnL || 0),
        0
    );
    const top5TradeCount = topTraded
        .slice(0, 5)
        .reduce((sum, row) => sum + (row.totalTrades || 0), 0);

    return {
        top5PnLSharePct: totalPositivePnL
            ? Number(((top5PositiveCombined / totalPositivePnL) * 100).toFixed(1))
            : 0,
        top5TradesSharePct: totalTrades
            ? Number(((top5TradeCount / totalTrades) * 100).toFixed(1))
            : 0
    };
};

const resolveOpenAccountsMetrics = (summary, openAccountsInScope) => {
    const hasOpenMetrics =
        summary != null && Object.prototype.hasOwnProperty.call(summary, 'openAccountsTotal');

    const openAccountsTotal = hasOpenMetrics
        ? summary.openAccountsTotal || 0
        : openAccountsInScope.length;

    const openAccountsWithTrades = hasOpenMetrics ? summary.openAccountsWithTrades || 0 : 0;

    let openAccountsWithTradesPct = 0;
    if (hasOpenMetrics) {
        openAccountsWithTradesPct = summary.openAccountsWithTradesPct || 0;
    } else if (openAccountsTotal > 0) {
        openAccountsWithTradesPct = Number(
            ((openAccountsWithTrades / openAccountsTotal) * 100).toFixed(1)
        );
    }

    return {
        openAccountsTotal,
        openAccountsWithTrades,
        openAccountsWithTradesPct
    };
};

export default function AdminTradingAnalytics({
    selectedFundedType,
    openAccountsInScope = [],
    usePaidAccountStatsScope = false,
    isTtfStatsType = false
}) {
    const defaultRange = useMemo(() => {
        const today = nyNow();
        return [today.subtract(6, 'day').startOf('day'), today.endOf('day')];
    }, []);

    const [dateRange, setDateRange] = useState(defaultRange);
    const [activePresetKey, setActivePresetKey] = useState(null);
    const [activeRange, setActiveRange] = useState(null);
    const [eligibleOnly, setEligibleOnly] = useState(true);
    const [positivePnLOnly, setPositivePnLOnly] = useState(false);
    const [excludeOverPayoutCap, setExcludeOverPayoutCap] = useState(false);
    const [capAccountPeriodPnLAt10k, setCapAccountPeriodPnLAt10k] = useState(true);

    const apiTypes = mapSelectedTypeToApiTypes(selectedFundedType);
    const showOrders = apiTypes.includes('simulator_ttf');
    const startedAfter = isTtfStatsType && usePaidAccountStatsScope ? '2025-10-24' : undefined;

    const { data, isLoading, isError, error, mutate, isIdle } = useMutation(
        (payload) => fetchTradingAnalytics(payload),
        {
            retry: false,
            onError: (queryError) => {
                const apiError = queryError?.response?.data?.error;
                message.error(
                    apiError || queryError?.message || 'Failed to load trading analytics'
                );
            }
        }
    );

    const openAccountsMetrics = useMemo(
        () => resolveOpenAccountsMetrics(data?.summary, openAccountsInScope),
        [data?.summary, openAccountsInScope]
    );

    const symbolConcentrationMetrics = useMemo(
        () =>
            resolveSymbolConcentrationMetrics(data?.concentration, data?.summary, data?.topTraded),
        [data?.concentration, data?.summary, data?.topTraded]
    );

    const listTablesQueryKey = activeRange
        ? `${activeRange.startDate}-${activeRange.endDate}-${selectedFundedType}-${eligibleOnly}-${positivePnLOnly}-${excludeOverPayoutCap}-${capAccountPeriodPnLAt10k}-${startedAfter || 'all'}`
        : 'idle';

    const buildQueryPayload = (range) => ({
        startDate: range.startDate,
        endDate: range.endDate,
        types: apiTypes,
        eligibleOnly,
        positivePnLOnly,
        excludeOverPayoutCap,
        capAccountPeriodPnLAt10k,
        startedAfter
    });

    const runQuery = (range) => {
        mutate(buildQueryPayload(range));
    };

    const runQueryForDates = (start, end) => {
        if (!start || !end) return false;

        const range = {
            startDate: toNyCalendarDateString(start),
            endDate: toNyCalendarDateString(end)
        };

        setActiveRange(range);
        runQuery(range);
        return true;
    };

    const handleQuery = () => {
        if (!dateRange?.[0] || !dateRange?.[1]) return;

        runQueryForDates(dateRange[0], dateRange[1]);
    };

    const applyPreset = (presetKey) => {
        const preset = TRADING_RANGE_PRESETS.find((item) => item.key === presetKey);
        if (!preset) return;

        const [start, end] = preset.getRange();
        setDateRange([start, end]);
        setActivePresetKey(presetKey);
        runQueryForDates(start, end);
    };

    const handlePresetChange = (presetKey) => {
        if (presetKey === CUSTOM_PRESET_KEY) {
            setActivePresetKey(null);
            return;
        }

        applyPreset(presetKey);
    };

    const handleRangeChange = (values) => {
        if (!values || values.length !== 2) return;
        setDateRange(values);
        setActivePresetKey(null);
    };

    const activeFilterCount = [
        !eligibleOnly,
        positivePnLOnly,
        !capAccountPeriodPnLAt10k,
        excludeOverPayoutCap
    ].filter(Boolean).length;

    const filterControl = (
        <Popover
            trigger="click"
            placement="bottomRight"
            overlayClassName="admin-analytics-bar__filters-popover"
            content={
                <div className="admin-analytics-bar__filters-menu">
                    <p className="admin-analytics-bar__filters-heading">Trade filters only</p>
                    <p className="admin-analytics-bar__filters-note">
                        These affect trade stats and Net P/L. Gross liability change always uses the
                        same rules as the liability chart. {ELIGIBLE_OPEN_FILTER_TIP}
                    </p>
                    <Checkbox
                        checked={eligibleOnly}
                        disabled={isLoading}
                        onChange={(event) => setEligibleOnly(event.target.checked)}
                    >
                        <Tooltip title={ELIGIBLE_OPEN_FILTER_TIP}>Eligible open only</Tooltip>
                    </Checkbox>
                    <Checkbox
                        checked={positivePnLOnly}
                        disabled={isLoading}
                        onChange={(event) => setPositivePnLOnly(event.target.checked)}
                    >
                        Profitable accounts only
                    </Checkbox>
                    <Checkbox
                        checked={capAccountPeriodPnLAt10k}
                        disabled={isLoading}
                        onChange={(event) => setCapAccountPeriodPnLAt10k(event.target.checked)}
                    >
                        Cap period P/L at $10k
                    </Checkbox>
                    <Checkbox
                        checked={excludeOverPayoutCap}
                        disabled={isLoading}
                        onChange={(event) => setExcludeOverPayoutCap(event.target.checked)}
                    >
                        Exclude accounts with P/L higher than $10k
                    </Checkbox>
                </div>
            }
        >
            <Button size="small" icon={<FilterOutlined />} disabled={isLoading}>
                Trade filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
            </Button>
        </Popover>
    );

    const queryErrorMessage = isError
        ? error?.response?.data?.error || error?.message || 'Failed to load trading analytics'
        : null;

    const tabItems = data
        ? [
              {
                  key: 'symbols',
                  label: 'Symbols',
                  children: (
                      <div className="admin-trading-analytics-tables">
                          <AnalyticsTableBlock title="Most traded">
                              <Table
                                  size="small"
                                  rowKey="symbol"
                                  columns={symbolColumns}
                                  dataSource={data.topTraded || []}
                                  pagination={false}
                              />
                          </AnalyticsTableBlock>
                          <AnalyticsTableBlock
                              title="Best symbols"
                              hint="Highest net P/L in the selected date range."
                          >
                              <Table
                                  size="small"
                                  rowKey="symbol"
                                  columns={symbolColumns}
                                  dataSource={data.profitableSymbols || []}
                                  pagination={false}
                              />
                          </AnalyticsTableBlock>
                          <AnalyticsTableBlock
                              title="Worst symbols"
                              hint="Largest net losses in the selected date range."
                          >
                              <Table
                                  size="small"
                                  rowKey="symbol"
                                  columns={symbolColumns}
                                  dataSource={data.toxicSymbols || []}
                                  pagination={false}
                              />
                          </AnalyticsTableBlock>
                          <AnalyticsTableBlock
                              title="Commission drag by symbol"
                              hint="Commissions as % of gross P/L — high values mean hidden bleed."
                          >
                              <Table
                                  size="small"
                                  rowKey="symbol"
                                  columns={commissionDragColumns}
                                  dataSource={data.commissionDragBySymbol || []}
                                  pagination={false}
                              />
                          </AnalyticsTableBlock>
                          {showOrders && (
                              <AnalyticsTableBlock title="Simulator fills (orders DB)">
                                  <Table
                                      size="small"
                                      rowKey="ticker"
                                      columns={orderColumns}
                                      dataSource={data.ordersByTicker || []}
                                      pagination={false}
                                  />
                              </AnalyticsTableBlock>
                          )}
                      </div>
                  )
              },
              {
                  key: 'sessions',
                  label: 'Sessions',
                  children: (
                      <div className="admin-trading-analytics-tables">
                          <AnalyticsTableBlock
                              title="P/L by session (ET)"
                              hint="Grouped by Eastern Time hour of trade close (open trades use open time). Each row sums all hours in that window."
                          >
                              <Table
                                  size="small"
                                  rowKey="key"
                                  columns={sessionColumns}
                                  dataSource={data.pnlBySession || []}
                                  pagination={false}
                              />
                          </AnalyticsTableBlock>
                          <AnalyticsTableBlock title="P/L by day of week">
                              <Table
                                  size="small"
                                  rowKey="dayOfWeek"
                                  columns={dayColumns}
                                  dataSource={data.pnlByDayOfWeek || []}
                                  pagination={false}
                              />
                          </AnalyticsTableBlock>
                      </div>
                  )
              },
              {
                  key: 'style',
                  label: 'Style',
                  children: (
                      <div className="admin-trading-analytics-tables">
                          <AnalyticsTableBlock
                              title="P/L by hold time"
                              hint="Scalp vs intraday vs swing buckets."
                          >
                              <Table
                                  size="small"
                                  rowKey="key"
                                  columns={holdTimeColumns}
                                  dataSource={data.pnlByHoldTime || []}
                                  pagination={false}
                              />
                          </AnalyticsTableBlock>
                          <AnalyticsTableBlock
                              title="Long vs short P/L"
                              hint="Directional bias across the book."
                          >
                              <Table
                                  size="small"
                                  rowKey="side"
                                  columns={sideColumns}
                                  dataSource={data.longVsShort || []}
                                  pagination={false}
                              />
                          </AnalyticsTableBlock>
                      </div>
                  )
              },
              {
                  key: 'accounts',
                  label: 'Accounts & risk',
                  children: (
                      <>
                          <div className="admin-trading-analytics-pair-row">
                              <AnalyticsTableBlock
                                  layout="half"
                                  title="Best accounts"
                                  hint="Top 3 by period net P/L. Total net P/L is lifetime (equity − initial), capped at $10k when that filter is on."
                              >
                                  <Table
                                      className="admin-trader-leaderboard-table"
                                      size="small"
                                      tableLayout="fixed"
                                      rowKey="accountUniqueId"
                                      columns={accountLeaderboardColumns}
                                      dataSource={data.topTraders || []}
                                      pagination={false}
                                      locale={{ emptyText: 'No accounts in range' }}
                                  />
                              </AnalyticsTableBlock>
                              <AnalyticsTableBlock
                                  layout="half"
                                  title="Worst accounts"
                                  hint="Bottom 3 by period net P/L. Total net P/L is lifetime (equity − initial), capped at $10k when that filter is on."
                              >
                                  <Table
                                      className="admin-trader-leaderboard-table"
                                      size="small"
                                      tableLayout="fixed"
                                      rowKey={(row) => `worst-${row.accountUniqueId}`}
                                      columns={accountLeaderboardColumns}
                                      dataSource={data.worstTraders || []}
                                      pagination={false}
                                      locale={{ emptyText: 'No accounts in range' }}
                                  />
                              </AnalyticsTableBlock>
                          </div>
                          {data.summary?.payoutRuleFlagCount > 0 && (
                              <Alert
                                  type="warning"
                                  showIcon
                                  style={{ marginBottom: 12 }}
                                  message={`${data.summary.payoutRuleFlagCount} account(s) may trigger the 30% largest-win payout cap`}
                                  description="Review before approving payouts. Adjusted P/L shows payout-eligible amount if the cap applies."
                              />
                          )}
                          <div className="admin-trading-analytics-tables admin-trading-analytics-tables--accounts">
                              <AnalyticsTableBlock
                                  layout="full"
                                  title="30% payout rule flags"
                                  hint="Largest single win ≥ 30% of account total P/L in range (positive P/L only)."
                              >
                                  <Table
                                      key={`payout-rule-flags-${listTablesQueryKey}`}
                                      size="small"
                                      rowKey="accountUniqueId"
                                      columns={payoutFlagColumns}
                                      dataSource={data.payoutRuleFlags || []}
                                      pagination={LONG_LIST_TABLE_PAGINATION}
                                      scroll={{ x: 720 }}
                                      locale={{ emptyText: 'No flagged accounts in range' }}
                                  />
                              </AnalyticsTableBlock>
                              {data.largestWinTrades?.length > 0 && (
                                  <AnalyticsTableBlock
                                      layout="full"
                                      title="Largest winning trades (flagged accounts)"
                                  >
                                      <Table
                                          size="small"
                                          rowKey={(row) =>
                                              `${row.accountUniqueId}-${row.symbol}-${row.closeTime}`
                                          }
                                          columns={largestWinTradeColumns}
                                          dataSource={data.largestWinTrades || []}
                                          pagination={false}
                                          scroll={{ x: 480 }}
                                          locale={{ emptyText: 'No flagged wins' }}
                                      />
                                  </AnalyticsTableBlock>
                              )}
                              <div className="admin-trading-analytics-pair-row">
                                  <AnalyticsTableBlock
                                      layout="half"
                                      title="Top accounts by P/L"
                                      hint="Who drives liability / profit."
                                  >
                                      <Table
                                          size="small"
                                          rowKey="accountUniqueId"
                                          columns={accountColumns}
                                          dataSource={data.topAccounts || []}
                                          pagination={false}
                                          scroll={{ x: 440 }}
                                      />
                                  </AnalyticsTableBlock>
                                  <AnalyticsTableBlock layout="half" title="Bottom accounts by P/L">
                                      <Table
                                          size="small"
                                          rowKey="accountUniqueId"
                                          columns={accountColumns}
                                          dataSource={data.bottomAccounts || []}
                                          pagination={false}
                                          scroll={{ x: 440 }}
                                      />
                                  </AnalyticsTableBlock>
                              </div>
                              <AnalyticsTableBlock
                                  layout="full"
                                  title="Multi-account users"
                                  hint="Users with 2+ funded accounts in program scope (current state). Red tags = risk violation."
                              >
                                  <Table
                                      key={`multi-funded-users-${listTablesQueryKey}`}
                                      size="small"
                                      rowKey="userId"
                                      columns={multiAccountColumns}
                                      dataSource={data.multiFundedUsers || []}
                                      pagination={LONG_LIST_TABLE_PAGINATION}
                                      scroll={{ x: 640 }}
                                      locale={{ emptyText: 'No multi-account users' }}
                                  />
                              </AnalyticsTableBlock>
                          </div>
                      </>
                  )
              }
          ]
        : [];

    return (
        <div className="admin-analytics-panel admin-stats-trading-section">
            <AdminAnalyticsQueryBar
                title="Trading Analytics"
                hint="Trade activity for the selected range · click Run after changing custom dates"
                presets={TRADING_RANGE_PRESETS}
                dateRange={dateRange}
                onDateRangeChange={handleRangeChange}
                activePresetKey={activePresetKey}
                onPresetChange={handlePresetChange}
                onRun={handleQuery}
                runLabel="Run"
                loading={isLoading}
                filters={filterControl}
            />

            <div className="admin-analytics-panel__body">
                {queryErrorMessage && (
                    <Alert
                        type="error"
                        showIcon
                        className="admin-analytics-panel__alert"
                        message="Could not load analytics"
                        description={queryErrorMessage}
                    />
                )}

                {isIdle && !isLoading && (
                    <p className="admin-analytics-panel__placeholder">
                        Choose a range above to load trading analytics.
                    </p>
                )}

                {isLoading && (
                    <div className="admin-analytics-panel__loading">
                        <Spin size="small" />
                    </div>
                )}

                {data && !isError && (
                    <>
                        {((data.scope?.excludeOverPayoutCap &&
                            data.scope?.accountsExcludedFromPnL > 0) ||
                            (data.scope?.capAccountPeriodPnLAt10k &&
                                data.scope?.accountsPeriodPnLCapped > 0)) && (
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginTop: 12 }}
                                message={[
                                    data.scope?.accountsExcludedFromPnL > 0
                                        ? `${data.scope.accountsExcludedFromPnL} account(s) excluded (P/L higher than $10k)`
                                        : null,
                                    data.scope?.accountsPeriodPnLCapped > 0
                                        ? `${data.scope.accountsPeriodPnLCapped} account(s) period P/L capped at $10k`
                                        : null
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                description="P/L exclusion removes those accounts entirely from trade stats. Period cap limits each account’s contribution to $10k in this range."
                            />
                        )}
                        <div className="admin-stats-trading-metrics">
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title={
                                        <StatTitle
                                            title="Gross liability Δ"
                                            tip={`End of range (${data.range?.endDate || '—'}) minus day before range start (${data.summary?.grossLiabilityBaselineDate || '—'}). Same calculation as the Gross Potential Liability chart.`}
                                        />
                                    }
                                    value={data.summary?.grossLiabilityChange || 0}
                                    precision={2}
                                    prefix="$"
                                    valueStyle={{
                                        color:
                                            (data.summary?.grossLiabilityChange || 0) >= 0
                                                ? '#389e0d'
                                                : '#cf1322'
                                    }}
                                />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title={
                                        <StatTitle
                                            title="Gross liability (end)"
                                            tip="Gross Potential Liability at end of the selected range (matches stats panel when the range ends today)."
                                        />
                                    }
                                    value={data.summary?.grossLiabilityEnd || 0}
                                    precision={2}
                                    prefix="$"
                                />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic title="Trades" value={data.summary?.totalTrades || 0} />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title={
                                        <StatTitle
                                            title="Accounts traded"
                                            tip="Accounts in the filtered scope with at least one trade in the selected date range."
                                        />
                                    }
                                    value={data.summary?.accountsTradedInPeriod || 0}
                                />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title={
                                        <StatTitle
                                            title="Winners / losers"
                                            tip="Among filtered accounts that traded in the range: period net P/L positive vs negative (breakeven excluded)."
                                        />
                                    }
                                    value={data.summary?.accountsWinnersInPeriod ?? 0}
                                    suffix={` / ${data.summary?.accountsLosersInPeriod ?? 0}`}
                                />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title={
                                        <span>
                                            Net P/L
                                            {(data?.scope?.excludeOverPayoutCap ||
                                                data?.scope?.capAccountPeriodPnLAt10k) && (
                                                <>
                                                    {' '}
                                                    <Tooltip
                                                        title={[
                                                            data?.scope?.excludeOverPayoutCap
                                                                ? 'Excludes accounts with P/L (equity − initial) higher than $10k.'
                                                                : null,
                                                            data?.scope?.capAccountPeriodPnLAt10k
                                                                ? 'Caps each account’s period net P/L contribution at $10k (proportional across its trades).'
                                                                : null
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' ')}
                                                    >
                                                        <InfoCircleOutlined
                                                            style={{ color: 'rgba(0,0,0,0.35)' }}
                                                        />
                                                    </Tooltip>
                                                </>
                                            )}
                                        </span>
                                    }
                                    value={data.summary?.totalPnL || 0}
                                    precision={2}
                                    prefix="$"
                                    valueStyle={{
                                        color:
                                            (data.summary?.totalPnL || 0) >= 0
                                                ? '#389e0d'
                                                : '#cf1322'
                                    }}
                                />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title="Win rate"
                                    value={data.summary?.winRate || 0}
                                    suffix="%"
                                />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title="Avg win / loss"
                                    value={data.summary?.avgWin || 0}
                                    formatter={() => (
                                        <span>
                                            <span style={{ color: '#389e0d' }}>
                                                {formatCurrency(data.summary?.avgWin || 0)}
                                            </span>
                                            <span
                                                style={{
                                                    margin: '0 4px',
                                                    color: 'rgba(0, 0, 0, 0.45)'
                                                }}
                                            >
                                                /
                                            </span>
                                            <span style={{ color: '#cf1322' }}>
                                                {formatCurrency(data.summary?.avgLoss || 0)}
                                            </span>
                                        </span>
                                    )}
                                />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title="Symbols traded"
                                    value={data.summary?.uniqueSymbols || 0}
                                />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title={
                                        <StatTitle
                                            title="Open accounts w/ trades"
                                            tip="Open funded accounts with at least one trade in the date range, as a percent of all open funded accounts in scope."
                                        />
                                    }
                                    value={openAccountsMetrics.openAccountsWithTradesPct}
                                    suffix="%"
                                    precision={1}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {openAccountsMetrics.openAccountsWithTrades}
                                    {' / '}
                                    {openAccountsMetrics.openAccountsTotal}
                                    {' open'}
                                </Text>
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title={
                                        <StatTitle
                                            title="Top 5 symbols P/L"
                                            tip="Share of total positive symbol P/L from the five symbols with the highest positive P/L in the range."
                                        />
                                    }
                                    value={symbolConcentrationMetrics.top5PnLSharePct}
                                    suffix="%"
                                />
                            </div>
                            <div className="admin-stats-trading-metric">
                                <Statistic
                                    title={
                                        <StatTitle
                                            title="Top 5 symbols trades"
                                            tip="Share of all trades that occurred in the five most-traded symbols."
                                        />
                                    }
                                    value={symbolConcentrationMetrics.top5TradesSharePct}
                                    suffix="%"
                                />
                            </div>
                        </div>

                        {data.concentration?.symbols?.top5Items?.length > 0 && (
                            <Text
                                type="secondary"
                                style={{ display: 'block', marginTop: 8, marginBottom: 12 }}
                            >
                                Top symbols:{' '}
                                {data.concentration.symbols.top5Items
                                    .map(
                                        (item) => `${item.label} (${formatCurrency(item.totalPnL)})`
                                    )
                                    .join(', ')}
                            </Text>
                        )}

                        <Tabs items={tabItems} size="small" className="admin-stats-trading-tabs" />
                    </>
                )}
            </div>
        </div>
    );
}

AdminTradingAnalytics.propTypes = {
    selectedFundedType: PropTypes.string,
    openAccountsInScope: PropTypes.arrayOf(
        PropTypes.shape({
            accountUniqueId: PropTypes.string
        })
    ),
    usePaidAccountStatsScope: PropTypes.bool,
    isTtfStatsType: PropTypes.bool
};

AdminTradingAnalytics.defaultProps = {
    selectedFundedType: 'ALL',
    openAccountsInScope: [],
    usePaidAccountStatsScope: false,
    isTtfStatsType: false
};
